// End a therapist's Emergency Pause early (0352) — TEND-061 "Support controls
// continuation". Superadmin only: who "Support" is has not been decided, so the
// button is not offered to Admin or Hub Manager (Open Item for Luigi).

import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Textarea } from "@bednbody/ui/components/textarea";
import { useEffect, useState } from "react";
import type { TherapistSessionTodayRow } from "./therapist-sessions.hooks";

interface EndEmergencyPauseDialogProps {
  session: TherapistSessionTodayRow | null;
  isPending: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}

export function EndEmergencyPauseDialog({
  session,
  isPending,
  errorMessage,
  onClose,
  onSubmit,
}: EndEmergencyPauseDialogProps) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (session) setReason("");
  }, [session]);

  const trimmedReason = reason.trim();
  const canSubmit = trimmedReason.length > 0 && !isPending;

  return (
    <Dialog
      open={session !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>End emergency break</DialogTitle>
          <DialogDescription>
            {session ? `${session.therapistName} · ${session.hubName}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-warning">
            She reported an issue and is on an emergency break, so she gets no bookings. Ending it
            puts her back on Auto-Assign now. Only do this once you have checked she is safe and
            able to work. Her issue report stays on file.
          </p>

          <div className="space-y-2">
            <label htmlFor="end-emergency-reason" className="block text-sm font-medium">
              Reason (required)
            </label>
            <Textarea
              id="end-emergency-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Called her — issue resolved, ready to work"
              rows={3}
            />
          </div>

          {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={() => onSubmit(trimmedReason)}>
            {isPending ? "Ending…" : "End break"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
